<template>
  <div class="md-layout">
    <help-link :link="helpLink.link"></help-link>
    <div class="md-layout-item md-size-100" data-tour="telemetry_card">
      <md-card class="md-card-stats">
        <md-card-header class="md-card-header-icon md-card-header-primary">
          <div class="card-icon">
            <md-icon>bar_chart</md-icon>
          </div>
          <md-toolbar class="fm-card-header">
            <h1 class="md-title">
              <span>Application Telemetry:</span>{{ appLabel }}
            </h1>
            <app-selector
              @update-selected-app="changeMySelectedApp"
            ></app-selector>
          </md-toolbar>
        </md-card-header>
      </md-card>
    </div>
    <div
      class="md-layout-item md-medium-size-50 md-xsmall-size-100 md-size-25"
      data-tour="clients_card"
    >
      <stats-card header-color="fm-blue">
        <template slot="header">
          <div class="card-icon">
            <md-icon>group</md-icon>
          </div>
          <p class="category">Clients</p>
          <h3 class="title" :key="appStatsKey" data-tour="clients_number">
            {{ appStats.clients || "0" }}
          </h3>
        </template>

        <template slot="footer">
          <div class="stats">
            <md-icon>update</md-icon>
            {{ appStatsLastUpdated }}
          </div>
        </template>
      </stats-card>
    </div>
    <div
      class="md-layout-item md-medium-size-50 md-xsmall-size-100 md-size-25"
      data-tour="connections_card"
    >
      <stats-card header-color="fm-pink">
        <template slot="header">
          <div class="card-icon">
            <md-icon>cast_connected</md-icon>
          </div>
          <p class="category">Connections</p>
          <h3 class="title" :key="appStatsKey" data-tour="connections_number">
            {{ appStats.connections || "0" }}
          </h3>
        </template>

        <template slot="footer">
          <div class="stats">
            <md-icon>update</md-icon>
            {{ appStatsLastUpdated }}
          </div>
        </template>
      </stats-card>
    </div>
    <div
      class="md-layout-item md-medium-size-50 md-xsmall-size-100 md-size-25"
      data-tour="channels_card"
    >
      <stats-card header-color="fm-blue">
        <template slot="header">
          <div class="card-icon">
            <md-icon>view_module</md-icon>
          </div>
          <p class="category">Channels</p>
          <h3 class="title" :key="appStatsKey" data-tour="channels_number">
            {{ appStats.channels || "0" }}
          </h3>
        </template>

        <template slot="footer">
          <div class="stats">
            <md-icon>update</md-icon>
            {{ appStatsLastUpdated }}
          </div>
        </template>
      </stats-card>
    </div>
    <div
      class="md-layout-item md-medium-size-50 md-xsmall-size-100 md-size-25"
      data-tour="bandwidth_card"
    >
      <stats-card header-color="fm-pink">
        <template slot="header">
          <div class="card-icon">
            <md-icon>network_check</md-icon>
          </div>
          <p class="category">Bandwidth</p>
          <h3 class="title" :key="appStatsKey" data-tour="bandwidth_number">
            {{ displayedBandwidth }}
            ({{ displayedBandwidthUnit }})
          </h3>
        </template>

        <template slot="footer">
          <div class="stats">
            <md-icon>update</md-icon>
            {{ appStatsLastUpdated }}
          </div>
        </template>
      </stats-card>
    </div>
    <div
      class="md-layout-item md-medium-size-100 md-xsmall-size-100 md-size-50"
      data-tour="connections_per_hour_chart"
    >
      <chart-card
        :key="connectionsPerHourKey"
        :chart-data="connectionsPerHourChart.data"
        :chart-options="connectionsPerHourChart.options"
        :chart-responsive-options="connectionsPerHourChart.responsiveOptions"
        chart-type="Bar"
        chart-inside-header
        background-color="fm-white"
      >
        <md-icon slot="fixed-button">build</md-icon>
        <md-button class="md-simple md-info md-just-icon" slot="first-button">
          <md-icon>refresh</md-icon>
          <md-tooltip md-direction="bottom">Refresh</md-tooltip>
        </md-button>
        <md-button class="md-simple md-just-icon" slot="second-button">
          <md-icon>edit</md-icon>
          <md-tooltip md-direction="bottom">Change Date</md-tooltip>
        </md-button>

        <template slot="footer">
          <h4 class="title">Connections per Hour</h4>
        </template>
        <template slot="action-link">
          <div>
            <md-button
              class="md-simple"
              @click.native="codepenConnection()"
              :disabled="!codepenEnabled"
            >
              Create a Connection
            </md-button>
            <md-button
              class="md-simple md-info md-just-icon"
              @click.native="loadHourlyConnectionStats()"
              :disabled="!hourlyConnectionsRefreshEnabled"
            >
              <md-icon>refresh</md-icon>
              <md-tooltip md-direction="bottom">Refresh</md-tooltip>
            </md-button>
          </div>
        </template>
      </chart-card>
    </div>
    <div
      class="md-layout-item md-medium-size-100 md-xsmall-size-100 md-size-50"
      data-tour="daily_connections_chart"
    >
      <chart-card
        :key="connectionsPerDayKey"
        :chart-data="connectionsPerDayChart.data"
        :chart-options="connectionsPerDayChart.options"
        chart-type="Line"
        chart-inside-header
        background-color="fm-white"
      >
        <md-button class="md-simple md-info md-just-icon" slot="first-button">
          <md-icon>refresh</md-icon>
          <md-tooltip md-direction="bottom">Refresh</md-tooltip>
        </md-button>
        <md-button class="md-simple md-just-icon" slot="second-button">
          <md-icon>edit</md-icon>
          <md-tooltip md-direction="bottom">Change Date</md-tooltip>
        </md-button>

        <template slot="footer">
          <h4 class="title">Connections per Day</h4>
        </template>
        <template slot="action-link">
          <div>
            <md-button
              class="md-simple"
              @click.native="codepenConnection()"
              :disabled="!codepenEnabled"
            >
              Create a Connection
            </md-button>
            <md-button
              class="md-simple md-info md-just-icon"
              @click.native="loadDailyConnectionStats()"
              :disabled="!dailyConnectionsRefreshEnabled"
            >
              <md-icon>refresh</md-icon>
              <md-tooltip md-direction="bottom">Refresh</md-tooltip>
            </md-button>
          </div>
        </template>
      </chart-card>
    </div>
    <div class="md-layout-item md-size-100" data-tour="usage_stats_card">
      <md-card class="md-card-stats">
        <md-card-header class="md-card-header-icon md-card-header-fm-blue">
          <div class="card-icon">
            <md-icon>pie_chart</md-icon>
          </div>
          <md-toolbar class="fm-card-header">
            <h1 class="md-title">Usage Stats for: {{ appLabel }}</h1>
          </md-toolbar>
        </md-card-header>
        <md-card-content>
          <div class="md-layout">
            <div
              class="md-layout-item md-medium-size-50 md-xsmall-size-100 md-size-50"
            >
              <pie-chart
                ref="platform-share"
                :title="'Usage by Platform'"
                :seriesName="'Platform Share'"
                :noDataMsg="
                  'It looks like you have had no active connections for the selected Application during the last month.'
                "
              ></pie-chart>
            </div>
            <div
              class="md-layout-item md-medium-size-50 md-xsmall-size-100 md-size-50"
            >
              <pie-chart
                ref="browser-share"
                :title="'Web Usage by Browser'"
                :seriesName="'Browser Share'"
                :noDataMsg="
                  'It looks like you have had no active web connections for the selected Application during the last month.'
                "
              ></pie-chart>
            </div>
            <div
              class="md-layout-item md-medium-size-50 md-xsmall-size-100 md-size-50"
            >
              <pie-chart
                ref="version-share"
                :title="'Usage by SDK Version'"
                :seriesName="'Version Share'"
                :noDataMsg="
                  'It looks like you have had no active connections for the selected Application during the last month.'
                "
              ></pie-chart>
            </div>
          </div>
        </md-card-content>
      </md-card>
    </div>
    <div class="md-layout-item md-size-100" data-tour="global_connections_card">
      <md-card class="md-card-stats">
        <md-card-header class="md-card-header-icon md-card-header-fm-pink">
          <div class="card-icon">
            <md-icon>language</md-icon>
          </div>
          <md-toolbar class="fm-card-header">
            <h1 class="md-title">Unique IPs by Location</h1>
          </md-toolbar>
        </md-card-header>
        <md-card-content>
          <div class="md-layout">
            <div class="md-layout-item md-size-50">
              <md-table
                v-model="connectionsByCountry"
                class="md-table-global-sales"
              >
                <md-table-empty-state
                  md-label="No Data Found"
                  md-description="It looks like you have had no active connections for the selected Application during the last month."
                >
                </md-table-empty-state>
                <md-table-row slot="md-table-row" slot-scope="{ item }">
                  <md-table-cell md-label="">
                    <div class="flag">
                      <img
                        :src="
                          `./img/flags-100px/${item.country.toLowerCase()}.png`
                        "
                      />
                    </div>
                  </md-table-cell>
                  <md-table-cell>{{ item.country }}</md-table-cell>
                  <md-table-cell>{{ item.numberOfConnections }}</md-table-cell>
                  <md-table-cell
                    >{{
                      (
                        (item.numberOfConnections / globalConnectionsTotal) *
                        100
                      ).toFixed(2)
                    }}%</md-table-cell
                  >
                </md-table-row>
              </md-table>
            </div>
            <div class="md-layout-item md-size-50">
              <async-world-map
                :key="globalConnectionsKey"
                class="map"
                :data="mapData"
              ></async-world-map>
            </div>
          </div>
        </md-card-content>
      </md-card>
    </div>
  </div>
</template>
<script>
import api from "@/api";
import { get } from "vuex-pathify";
import _ from "lodash";
import { StatsCard, ChartCard } from "@/components";
import { HelpLinkTemplate, AppSelector } from "@/fmComponents";
import PieChart from "./PieChart.vue";
import AsyncWorldMap from "@/components/WorldMap/AsyncWorldMap.vue";
import { generateAppToken, autoCreateApp } from "../../utils/appcues";
import { globalSelectedApp, changeSelectedApp } from "../../utils/global";
import helpLinks from "../../contextualHelpLinks.js";
const parser = require('ua-parser-js');

export default {
  components: {
    StatsCard,
    ChartCard,
    AsyncWorldMap,
    PieChart,
    AppSelector,
    "help-link": HelpLinkTemplate
  },
  data() {
    return {
      selectedApp: null, // App the user is currently using to fetch stats.
      appToken: { value: null }, // Token used in the CodePen link
      appStatsKey: 0,
      appStatsIntervalId: null, // Used for stopping the AppStats interval.
      appStatsDelay: 10000, // duration (milliseconds) that we fetch AppStats on.
      appStatsCardTickIntervalId: null, // Used for stopping the AppStats Card Tick interval.
      appStatsTickDelay: 1000,
      secondsSinceAppStatsUpdate: 0, // "Last updated" ticker for AppStats.
      hourlyRefreshEnabled: false,
      dailyRefreshEnabled: false,
      disableChartRefreshTime: 15000, // length of time (milliseconds) chart refresh buttons are disabled when clicked.
      connectionsPerHourKey: 0,
      connectionsPerDayKey: 0,
      weekDayLabels: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
      connectionsPerDayChart: {
        data: {
          labels: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
          series: [[0, 0, 0, 0, 0, 0, 0]]
        },
        options: {
          lineSmooth: this.$Chartist.Interpolation.cardinal({
            tension: 0
          }),
          chartPadding: {
            top: 0,
            right: 0,
            bottom: 0,
            left: 0
          }
        }
      },
      connectionsPerHourChart: {
        data: {
          labels: [
            "12",
            "13",
            "14",
            "15",
            "16",
            "17",
            "18",
            "19",
            "20",
            "21",
            "22",
            "23",
            "24"
          ],
          series: [[0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]]
        },
        options: {
          axisX: {
            showGrid: false
          },
          chartPadding: {
            top: 0,
            right: 5,
            bottom: 0,
            left: 0
          }
        },
        responsiveOptions: [
          [
            "screen and (max-width: 640px)",
            {
              seriesBarDistance: 5,
              axisX: {
                labelInterpolationFnc: function(value) {
                  return value[0];
                }
              }
            }
          ]
        ]
      },
      globalConnectionsKey: 0,
      mapData: {},
      connectionsByCountry: [],
      helpLink: helpLinks.dashboard
    };
  },
  created() {
    var me = this;
    this.$store
      .dispatch("applications/loadEntities")
      .then(
        this.$store
          .dispatch(
            "subscription/loadSubscription",
            localStorage.subscriptionId
          )
          .then(result => {
            if (
              (result.status === "active" || result.status === "in_trial") &&
              !this.apps[0]
            ) {
              autoCreateApp();
            }
          })
      )
      .then(() => {
        if (this.apps[0]) {
          this.selectedApp = globalSelectedApp;
          if (this.selectedApp == null) {
            this.selectedApp = this.apps[0];
            changeSelectedApp(this.selectedApp);
          }
          this.loadSelectedApp(me);
        }
      })
      .catch(ex => {
        console.log(ex);
        /*
        TODO: Should we do something here? Update cards to show errors?
        The user has already been notified of the error ... what else needs to happen?
        */
      });
  },
  beforeDestroy() {
    // Clean up timers otherwise they keep firing even when other Vues are mounted.
    this.stopPolling();
  },
  computed: {
    apps: get("applications/entities") || [],
    appStats: get("appStats/entities") || {},
    hourlyConnectionStats: get("hourlyConnectionStats/entities") || [],
    dailyConnectionStats: get("dailyConnectionStats/entities") || [],
    appLabel: function() {
      return this.selectedApp
        ? this.selectedApp.tag || this.selectedApp.applicationId
        : "-";
    },
    appStatsLastUpdated: function() {
      if (!this.secondsSinceAppStatsUpdate) {
        return "Just updated.";
      } else {
        return `Updated ${this.secondsSinceAppStatsUpdate} seconds ago.`;
      }
    },
    estimatedBandwidth: function() {
      var result = 0;
      if (
        this.appStats &&
        this.appStats.inputBitrate > -1 &&
        this.appStats.outputBitrate > -1
      ) {
        result = this.appStats.inputBitrate + this.appStats.outputBitrate;
      }
      return result;
    },
    displayedBandwidth: function() {
      if (this.estimatedBandwidth > 1000000) {
        return Math.ceil(this.estimatedBandwidth / 1000000);
      }

      if (this.estimatedBandwidth > 1000) {
        return Math.ceil(this.estimatedBandwidth / 1000);
      }

      return this.estimatedBandwidth;
    },
    displayedBandwidthUnit: function() {
      if (this.estimatedBandwidth > 1000000) {
        return "Gbps";
      }

      if (this.estimatedBandwidth > 1000) {
        return "Mbps";
      }

      return "Kbps";
    },
    globalConnectionsTotal: function() {
      return this.connectionsByCountry.reduce(
        (a, b) => a + (b["numberOfConnections"] || 0),
        0
      );
    },
    hourlyConnectionsRefreshEnabled: function() {
      return this.hourlyRefreshEnabled;
    },
    dailyConnectionsRefreshEnabled: function() {
      return this.dailyRefreshEnabled;
    },
    codepenEnabled: function() {
      return this.selectedApp && this.selectedApp.applicationId;
    }
  },
  methods: {
    getClass: function(headerColor) {
      return "md-card-header-" + headerColor + "";
    },
    startPolling: function() {
      this.stopPolling();

      // Start polling for AppStats.
      this.loadAppStats();
      this.appStatsCardTickIntervalId = setInterval(
        this.appStatsCardTick,
        this.appStatsTickDelay
      );
      this.appStatsIntervalId = setInterval(
        this.loadAppStats,
        this.appStatsDelay
      );
    },
    stopPolling: function() {
      if (this.appStatsIntervalId) {
        clearTimeout(this.appStatsIntervalId);
      }
      if (this.appStatsCardTickIntervalId) {
        clearTimeout(this.appStatsCardTickIntervalId);
      }
    },
    loadAppStats: function() {
      var me = this;
      this.$store
        .dispatch("appStats/loadEntities", [
          [{ k: "applicationId", v: me.selectedApp.applicationId }],
          false
        ])
        .then(() => {
          me.appStatsKey++;
          me.secondsSinceAppStatsUpdate = 0;
        })
        .catch(() => {
          /* TODO: Should we do something here? Clear timers and update cards to show errors? */
        });
    },
    loadHourlyConnectionStats: function() {
      var me = this;

      this.hourlyRefreshEnabled = false;
      setTimeout(() => {
        me.hourlyRefreshEnabled = true;
      }, this.disableChartRefreshTime);

      this.$store
        .dispatch("hourlyConnectionStats/loadEntities", [
          [
            { k: "applicationId", v: me.selectedApp.applicationId },
            { k: "tag", v: "hourly_connections" }
          ],
          false
        ])
        .then(() => {
          if (me.hourlyConnectionStats && me.hourlyConnectionStats.length) {
            me.connectionsPerHourChart.data.labels = me.hourlyConnectionStats.map(
              x => {
                return `${x.hourBucket}`;
              }
            );
            me.connectionsPerHourChart.data.series[0] = me.hourlyConnectionStats.map(
              x => {
                return x.count;
              }
            );
            me.connectionsPerHourKey++;
          }
        })
        .catch(() => {
          /* TODO: Should we do something here? */
        });
    },
    loadDailyConnectionStats: function() {
      var me = this;

      this.dailyRefreshEnabled = false;
      setTimeout(() => {
        me.dailyRefreshEnabled = true;
      }, this.disableChartRefreshTime);

      this.$store
        .dispatch("dailyConnectionStats/loadEntities", [
          [
            { k: "applicationId", v: me.selectedApp.applicationId },
            { k: "tag", v: "daily_connections" }
          ],
          false
        ])
        .then(() => {
          if (me.dailyConnectionStats && me.dailyConnectionStats.length) {
            me.connectionsPerDayChart.data.labels = me.dailyConnectionStats.map(
              x => {
                var dt = new Date(x.dateBucket);
                return me.weekDayLabels[dt.getDay()];
              }
            );
            me.connectionsPerDayChart.data.series[0] = me.dailyConnectionStats.map(
              x => {
                return x.count;
              }
            );
            me.connectionsPerDayKey++;
          }
        })
        .catch(() => {
          /* TODO: Should we do something here? */
        });
    },
    appStatsCardTick: function() {
      this.secondsSinceAppStatsUpdate++;
    },
    loadUsageStatsForTag: function(tag, onSuccess) {
      var me = this;
      var end = this.$store.getters.sqlTimestampGenerator(0);
      var start = this.$store.getters.sqlTimestampGenerator(30); // one month

      api
        .getEntity(api.props.endPoints.dashboardQuery, null, [
          { k: "tag", v: tag },
          { k: "applicationId", v: me.selectedApp.applicationId },
          { k: "start", v: start },
          { k: "end", v: end }
        ])
        .then(results => {
          if (!results) {
            results = []; // handle `204`
          }

          if (typeof onSuccess === "function") {
            onSuccess(results);
          }
        })
        .catch(() => {
          /* TODO: Should we do something here? */
        });
    },
    connectionsByCountrySuccess: function(results) {
      this.connectionsByCountry = results;
      this.mapData = _.chain(results)
        .keyBy("country")
        .mapValues("numberOfConnections")
        .value();
      this.globalConnectionsKey++;
    },
    usageByPlatformSuccess: function(results) {
      this.$refs["platform-share"].bootstrap(
        results.map(x => {
          return {
            name: x.platform,
            y: x.registrations
          };
        })
      );
    },
    clientVersionSuccess: function(results) {
      this.$refs["version-share"].bootstrap(
        results.map(x => {
          return {
            name: x.version,
            y: x.count
          };
        })
      );
    },
    browserShareSuccess: function(results) {
      var countsByBrowser = {};
      var countsByVersionedBrowser = {};
      _.each(results, function(result) {
        var ua = parser(result.userAgent);

        const name = (ua && ua.browser && ua.browser.name) ? ua.browser.name : "Unknown";
        const version = (ua && ua.browser && ua.browser.version) ? ua.browser.version : "Unknown";
        const browserVersionKey = `${name}-${version}`;

        countsByBrowser[name] = (countsByBrowser[name] || 0) + result.count;
        countsByVersionedBrowser[browserVersionKey] =
            (countsByVersionedBrowser[browserVersionKey] || 0) + result.count;
      });
      var series = Object.keys(countsByBrowser).map(name => ({
        name,
        y: countsByBrowser[name],
        drilldown: name
      }));
      var drilldowns = Object.keys(countsByBrowser).map(name => ({
        name,
        id: name,
        data: Object.keys(countsByVersionedBrowser).reduce(function(
          result,
          versionedName
        ) {
          if (versionedName.startsWith(name)) {
            result.push({
              name: `${versionedName.split("-")[1]}`,
              y: countsByVersionedBrowser[versionedName],
              drilldown: versionedName
            });
          }
          return result;
        },
        [])
      }));

      this.$refs["browser-share"].bootstrap(series, drilldowns);
    },
    changeMySelectedApp: async function(newApp) {
      var me = this;
      this.selectedApp = newApp;
      changeSelectedApp(this.selectedApp);
      this.loadSelectedApp(me);
    },
    loadSelectedApp(me) {
      this.$store.dispatch("applications/loadEntities").then(() => {
        if (this.selectedApp != undefined) {
          //Do not attempt to load an app that doesn't exist (e.g. before user starts a subscription/trial they have none)
          generateAppToken(
            this.selectedApp.applicationId,
            this.selectedApp.sharedSecret,
            this.appToken
          );
          me.startPolling();
          me.loadHourlyConnectionStats();
          me.loadDailyConnectionStats();
          me.loadUsageStatsForTag(
            "geo_location_historical",
            this.connectionsByCountrySuccess
          );
          me.loadUsageStatsForTag(
            "usage_by_platform",
            this.usageByPlatformSuccess
          );
          me.loadUsageStatsForTag("browser_share", this.browserShareSuccess);
          me.loadUsageStatsForTag("client_version", this.clientVersionSuccess);
        }
      });
    },
    codepenConnection: function() {
      var gatewayUrl = api.props.config.codepenGatewayUrl;
      window.open(
        "https://codepen.io/frznmtn/pen/abbxYpe?token=" +
          this.appToken.value +
          "&appId=" +
          this.selectedApp.applicationId +
          "&gwUrl=" +
          gatewayUrl,
        "_blank"
      );
    }
  }
};
</script>
<style lang="scss">
.fm-card-header h1 {
  font-size: x-large;
}
.fm-card-header h1 span {
  font-weight: bold;
  margin-right: 1em;
}
.md-card-stats .md-card-header.md-card-header-icon button i {
  font-size: 1.1rem !important;
}
.md-card
  [class*="md-card-header-"]:not(.md-card-header-icon):not(.md-card-header-text):not(.md-card-header-image) {
  border: 1px solid #e8e8e8;
}

.question-mark {
  display: inline;
  font-size: 16px !important;
  color: #bbbbbb !important;
}

.question-mark img {
  width: 100%;
}

select.md-input {
  background-image: url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAYCAYAAADgdz34AAAA3klEQVRIS+3VMU9CMRTF8d8zBL+aizoQFhx0kUk33RzdYMNFXUFnYeGrYYyaJiUxJHDLSxodbNKpfeffc9/pbaPyaCrr+3OA++z4rtT5Pg5GuMnCY9yWQEoBE1xhlUUP8YDrCBIB0vojLvGO0yz4hm4JJAKcYYoPHGOZAUdYoIMBXrc5iQAHeMlzviFygj7O8dkWEJU4XI8chALRhn9AVKHf70VRTHu4wFfbmKZLNKt50dLBnna0imcMd/2I0phWa3Y/D1e1Xa9BCZJG0VuQNpaWKMx72xS1Fl5/WN3BN+AgJhnZQlq4AAAAAElFTkSuQmCC") !important;
  background-position: 100%, 100% 0 !important;
  background-size: 1.5em 1.5em !important;
  background-repeat: no-repeat !important;
}

.badge-free {
  background-color: #f24040;
}
.badge-free:hover,
.badge-free:focus {
  background-color: darken(#f24040, 5%);
}

.badge-hyper {
  background-color: #dd26bb;
}
.badge-hyper:hover,
.badge-hyper:focus {
  background-color: darken(#dd26bb, 5%);
}

.badge-growth {
  background-color: #36ddd1;
}
.badge-growth:hover,
.badge-growth:focus {
  background-color: darken(#36ddd1, 5%);
}
</style>
<style lang="scss" scoped>
.contextual-help {
  position: absolute;
  top: 23.5px;
  left: 130px;
  z-index: 100;
}
@media only screen and (max-width: 600px) {
  /deep/ .md-title span {
    display: none;
  }
  /deep/ .md-title {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin: 0;
  }

  /deep/ div.fm-card-header div.md-ripple {
    padding: 12px 0px !important;
  }
  /deep/ .md-card-header .fm-card-header {
    padding: 10px 0px;
  }
}
</style>
